/**
 * The poller — the print device's own loop. Port of web `utils/polling.js` (:41 `getPollingConfig`,
 * :80 `pollForTickets`, :117 `createDelayedTasks`, :196 `onUnprintedSalesSuccess`, :229/:253 the
 * two print loops, :641 `printTickets`), the canonical copy.
 *
 * A branch can nominate ONE device to print every kitchen ticket for the online channels, and one
 * to print item labels. That device asks the server for unprinted work, claims it, prints it, and
 * asks again. Everything about WHAT prints is already in this package — the poller only decides
 * WHEN to ask and what to do with the answer, so it is small, and it is pure: the fetch is a port
 * and the clock is injectable.
 *
 * v1's own cadence is kept (200 ms debounce · 1 s when busy · 10 s when idle · 15 s between cycles,
 * or 200 ms while FULL pages keep coming back and being claimed · ONE 4 s retry of a refused claim ·
 * stop on REST_TICKET_POLLING_UNAUTHORIZED), and so is its KICK: v1's `pollForTickets()` means "ask
 * again in 200 ms" even while the loop runs, and `start()` means the same here (see `kick`).
 * Two of its behaviours are deliberately NOT kept, both listed in docs/flows/PRINTING.md §9:
 *
 *   · v1 stops polling after ANY fetch error. Its error path sets `cancelDebounce = true` and then
 *     asks for a 15 s re-arm — which the debounce refuses BECAUSE that flag is set. One network
 *     blip and the print device goes quiet until something else calls `pollForTickets()`.
 *   · v1 re-polls from the ticket claim's success path but not from the label one, so a device that
 *     only prints labels polls once per 15 s timer rather than per cycle.
 *
 * One v1 behaviour IS kept, deliberately: a sale this device cannot print at all is not claimed, so
 * the server keeps listing it. Claiming would mark a KOT printed that no kitchen ever saw. What
 * this loop adds over v1 is the `unprintable` event — see `reportUnprintable`.
 */
import type { PrintContext, PrintReport, Printing } from "./types";

/** What v1's `getUnprintedTicketsFn` answers: sales with unprinted tickets, and with unprinted labels. */
export interface UnprintedSales {
  ticketSales?: any[];
  itemLabelSales?: any[];
}

export interface UnprintedRequest {
  pageSize: number;
  /** v1 `ticketsToggle` → `getTicket=1` */
  tickets: boolean;
  /** v1 `labelsToggle` → `getItemLabel=1` */
  labels: boolean;
}

/** The one port: how this host asks the server (or, offline, the device) for unprinted work. */
export type FetchUnprinted = (request: UnprintedRequest) => Promise<UnprintedSales>;

export interface PollerTimings {
  /** v1 `resetDelayedTask(200)` — how soon a cycle follows a start, a kick or a draining page */
  startMs: number;
  /** v1 retries every second while a cycle is in flight */
  busyMs: number;
  /** v1 `resetDelayedTask(10000)` — nothing to print */
  idleMs: number;
  /** v1's 15 s between cycles — used where a cycle claimed NOTHING (see `cycle`) */
  cycleMs: number;
  /** v1 `resetDelayedTask(15000)` after a fetch error */
  errorMs: number;
  /**
   * v1's `timeout = 4000 / retryCount; retryCount--`, entered with `retryCount = 1` from both its
   * print loops — so ONE retry, 4 s later. (Its `retryCount = 2` default is unreachable: every
   * caller passes a number.)
   */
  retryBaseMs: number;
  retries: number;
}

export const POLLER_TIMINGS: PollerTimings = {
  startMs: 200,
  busyMs: 1000,
  idleMs: 10000,
  cycleMs: 15000,
  errorMs: 15000,
  retryBaseMs: 4000,
  retries: 1,
};

/** v1 stops for good on this one — the device is no longer the branch's print device. */
export const POLLING_UNAUTHORIZED = "REST_TICKET_POLLING_UNAUTHORIZED";

export type PollerEvent =
  | { type: "cycle"; tickets: number; labels: number }
  | { type: "printed"; saleId?: string; report: PrintReport }
  | { type: "retry"; saleId?: string; inMs: number }
  /** this device printed nothing and claimed nothing — the sale comes back every cycle. See `reportUnprintable` */
  | { type: "unprintable"; saleId?: string; skipped: PrintReport["skipped"] }
  | { type: "error"; error: any }
  | { type: "stopped"; reason: "unauthorized" | "asked" };

export interface PollerDeps {
  printing: Printing;
  context(): PrintContext;
  fetchUnprinted: FetchUnprinted;
  timings?: Partial<PollerTimings>;
  /** for a host that wants to log or surface what the loop is doing; never required */
  onEvent?(event: PollerEvent): void;
}

export interface Poller {
  /**
   * v1 `pollForTickets()`: starts the loop — or, when it is already running, asks it to poll again
   * 200 ms from now (never mid-cycle, never later than it already would). There is still only ever
   * ONE loop; a second start is a kick, not a second loop.
   */
  start(): void;
  stop(): void;
  isRunning(): boolean;
}

/** v1 `getPollingConfig` — this device's role, read from the settings snapshot. */
export function pollingRole(ctx: PrintContext) {
  const tickets = ctx.posSettings?.tickets || {};
  const labels = ctx.posSettings?.itemLabelSettings || {};
  const isTicketPrinter = !!tickets.printDeviceId && tickets.printDeviceId === ctx.device.id;
  const isLabelPrinter = !!labels.printDeviceId && labels.printDeviceId === ctx.device.id;
  const hasTicketGroups = Array.isArray(tickets.groups) && tickets.groups.length > 0;
  return {
    isTicketPrinter,
    isLabelPrinter,
    // v1: a ticket print device with no groups configured cannot poll for tickets
    canPollTicket: isTicketPrinter && hasTicketGroups,
    canPollLabel: isLabelPrinter,
  };
}

const PENDING = "Pending";

/** v1 `createDelayedTasks`: `{ pageSize: 10 }`. A page this size coming back full means there is more. */
const PAGE_SIZE = 10;

export function createPoller(deps: PollerDeps): Poller {
  const timings: PollerTimings = { ...POLLER_TIMINGS, ...deps.timings };
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** the delay the armed timer was set with — see `kick` */
  let timerMs = 0;
  let running = false;
  let busy = false;
  /** a poll was asked for while a cycle was in flight: ask again the moment that cycle ends */
  let kicked = false;

  const emit = (event: PollerEvent) => deps.onEvent?.(event);

  function schedule(ms: number) {
    if (!running) return;
    if (timer) clearTimeout(timer);
    timerMs = ms;
    timer = setTimeout(() => {
      timer = undefined;
      // anything a cycle throws (a context snapshot that could not be read, say) must not end the
      // loop for the session: report it and try again, as after a failed fetch
      cycle().catch((error) => {
        emit({ type: "error", error });
        schedule(timings.errorMs);
      });
    }, ms);
  }

  /**
   * v1 `pollForTickets()` on a loop that is already running — "ask again soon". Web calls it on every
   * push notification (a new online order), on a 10 s timer, and when a polling channel's sale is
   * saved on the print device itself; the printing flow's own hand-off does the same. Ignoring it
   * left that work waiting for the loop's own timer: up to 10 s idle, 15 s after a batch, where v1
   * printed it about 200 ms later.
   *
   * Two refinements. A kick MID-cycle is remembered and made as that cycle finishes, where v1
   * retried every second until its cycle was over. And a kick never makes the ask LATER: a timer
   * already due within `startMs` is left alone, so a burst of kicks cannot keep pushing the next ask
   * back, as v1's debounce could. A kick on a loop stopped meanwhile does nothing — `schedule`
   * refuses a stopped loop.
   */
  function kick() {
    if (busy) {
      kicked = true;
      return;
    }
    if (timer && timerMs <= timings.startMs) return;
    schedule(timings.startMs);
  }

  function stop(reason: "unauthorized" | "asked" = "asked") {
    if (!running) return;
    running = false;
    if (timer) clearTimeout(timer);
    timer = undefined;
    emit({ type: "stopped", reason });
  }

  /** v1 `printSaleTicketsConfig`: what is still Pending, and whether the BILL prints with it. */
  function ticketWork(sale: any, ctx: PrintContext) {
    const pending = (sale?.tickets || []).filter((ticket: any) => ticket?.printStatus === PENDING);
    const role = pollingRole(ctx);
    // v1 prints the bill too when the sale was auto-accepted, nothing of it has printed yet, and
    // this device is the ticket print device — `printLog` of exactly one automatic entry
    const bill =
      role.isTicketPrinter &&
      sale?.fulfillmentStatus === "Accepted" &&
      sale?.tickets?.length === pending.length &&
      sale?.printLog?.length === 1 &&
      sale?.printLog?.[0]?.auto &&
      ctx.printers.defaultPrinter?.mid
        ? { printer: ctx.printers.defaultPrinter, copies: sale.printLog[0].printCount }
        : undefined;
    return { pending, bill };
  }

  /**
   * A sale that printed NOTHING and claimed NOTHING is one this device cannot do the work for —
   * the branch's label printer is not one it added, say, or the sale's only ticket routes nowhere
   * and there is no default printer to fall back to. The server is never told, so it lists the sale
   * again on the next cycle, and on every cycle after it.
   *
   * That relist is deliberate: claiming would mark a KOT "Completed" that no kitchen ever saw, and
   * the work has to stay queued until someone fixes the setting. What was missing is the SIGNAL —
   * v1's label path toasted `UI_ERR_SELECT_PRINTER_FROM_POS` on every cycle and its ticket path
   * said nothing at all, while this loop said nothing either way. Nothing in the poller reads this
   * event; it exists so a host can surface the condition (S4's `notifySkip` — which must NOT reach
   * the kiosk, where a customer would be shown a POS warning).
   */
  function reportUnprintable(sale: any, report: PrintReport) {
    if (report.jobs.length || report.claim) return;
    emit({ type: "unprintable", saleId: sale?.mid, skipped: report.skipped });
  }

  /**
   * One sale's tickets, with v1's retry when the STATUS WRITE was refused (not when a print fails).
   * Answers whether the claim was ACCEPTED — which is what tells the loop it made progress.
   */
  async function printTickets(sale: any, ctx: PrintContext, attempt = 0): Promise<boolean> {
    const { pending, bill } = ticketWork(sale, ctx);
    if (!pending.length) return false;

    const report = await deps.printing.print("tickets", { sale, tickets: pending, source: "Poller" });
    emit({ type: "printed", saleId: sale?.mid, report });
    if (attempt === 0) reportUnprintable(sale, report);

    if (report.claim === "rejected") {
      // v1 `failureCallback`: once, 4 s later — after that the sale waits for another cycle
      if (attempt < timings.retries) {
        const inMs = timings.retryBaseMs / (timings.retries - attempt);
        emit({ type: "retry", saleId: sale?.mid, inMs });
        await wait(inMs);
        // a loop stopped during that wait must not print: `stop()` means stop
        if (!running) return false;
        return printTickets(sale, ctx, attempt + 1);
      }
      return false;
    }
    // v1 prints the auto-accepted sale's bill after its tickets, inside the status write's SUCCESS
    // callback — so only once that claim was made and accepted. Without a claim nothing was marked
    // printed, the server lists the sale again, and the bill would print on every cycle.
    if (report.claim !== "accepted") return false;
    if (bill && String(sale?.saleStatus).toLowerCase() === "closed") {
      emit({ type: "printed", saleId: sale?.mid, report: await deps.printing.print("bill", { sale, ...bill }) });
    }
    return true;
  }

  /** One sale's labels. v1 `polling.printLabels` retries a REFUSED claim exactly as its ticket path does. */
  async function printLabels(sale: any, attempt = 0): Promise<boolean> {
    const report = await deps.printing.print("label", { sale, poller: true });
    emit({ type: "printed", saleId: sale?.mid, report });
    if (attempt === 0) reportUnprintable(sale, report);

    if (report.claim === "rejected" && attempt < timings.retries) {
      const inMs = timings.retryBaseMs / (timings.retries - attempt);
      emit({ type: "retry", saleId: sale?.mid, inMs });
      await wait(inMs);
      // a loop stopped during that wait must not print: `stop()` means stop
      if (!running) return false;
      return printLabels(sale, attempt + 1);
    }
    return report.claim === "accepted";
  }

  function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * v1 `printUnprintedSalesSequentially`: labels first, then tickets, one sale at a time.
   *
   * v1 needed a `taskQ` of sale ids here because it never awaited a print: its 15 s timer could
   * fire mid-batch and fetch the same sale again. This loop awaits every print and runs one cycle
   * at a time (`busy`), so the same sale cannot be in flight twice and the set is not needed — and
   * a sale that needs BOTH labels and tickets now gets both, where v1's taskQ made the ticket loop
   * skip it (the label claim's callback removed the id, so which one won was a race).
   */
  async function printAll(sales: UnprintedSales, ctx: PrintContext): Promise<boolean> {
    let progressed = false;
    // `stop()` means stop: the rest of a page is left for whoever polls next
    for (const sale of sales.itemLabelSales || []) {
      if (!running) return progressed;
      progressed = (await printLabels(sale)) || progressed;
    }
    for (const sale of sales.ticketSales || []) {
      if (!running) return progressed;
      progressed = (await printTickets(sale, ctx)) || progressed;
    }
    return progressed;
  }

  async function cycle(): Promise<void> {
    if (!running) return;
    const ctx = deps.context();
    const role = pollingRole(ctx);
    // v1 returns without re-arming: this device has nothing to poll for until something restarts it
    if (!role.canPollTicket && !role.canPollLabel) return stop("asked");
    // A timer only fires mid-cycle when a restart armed it while the old cycle was still printing
    // (`start` kept that ask for the cycle's end). Re-check in a second meanwhile; whatever the
    // finishing cycle schedules supersedes this timer — one timer, always.
    if (busy) return schedule(timings.busyMs);

    busy = true;
    try {
      let sales: UnprintedSales;
      try {
        sales = await deps.fetchUnprinted({ pageSize: PAGE_SIZE, tickets: role.isTicketPrinter, labels: role.isLabelPrinter });
      } catch (error: any) {
        emit({ type: "error", error });
        // v1 stops here for good — see this file's header
        if (error?.code === POLLING_UNAUTHORIZED) return stop("unauthorized");
        return schedule(timings.errorMs);
      }

      const tickets = sales?.ticketSales?.length || 0;
      const labels = sales?.itemLabelSales?.length || 0;
      emit({ type: "cycle", tickets, labels });
      if (!tickets && !labels) return schedule(timings.idleMs);

      let progressed = false;
      try {
        progressed = await printAll(sales, ctx);
      } catch (error) {
        emit({ type: "error", error });
      }
      // A FULL page means the server has more than it would hand over at once, and at 15 s a page
      // this device would fall behind at 10 sales per cycle. v1 drains instead — its ticket claim's
      // success path calls `pollForTickets()` and the debounce collapses the 15 s timer into 200 ms
      // — but it can only do that safely because its `taskQ` refuses a sale it has already claimed
      // for the rest of the window, and this loop deliberately has no such set (it awaits every
      // print, so nothing can overlap; §9 row 27). So the drain is narrowed to the case that needs
      // it: a full page, AND a claim accepted. A short page waits the cycle, and a sale this device
      // cannot claim at all (§9 rows 30–31) is asked for once per cycle — or once per outside kick
      // (web's 10 s timer), never faster — rather than in a hot loop.
      const morePending = tickets >= PAGE_SIZE || labels >= PAGE_SIZE;
      schedule(progressed && morePending ? timings.startMs : timings.cycleMs);
    } finally {
      // `schedule` only arms a timer, so the next cycle always finds this one finished
      busy = false;
      // an ask made while this cycle ran is made now (`kick`); on a loop stopped meanwhile it is a no-op
      if (kicked) {
        kicked = false;
        schedule(timings.startMs);
      }
    }
  }

  return {
    start() {
      if (running) return kick();
      running = true;
      // a restart while the previous cycle is still printing asks for a poll as well — and that
      // cycle's own closing schedule would otherwise replace the timer armed below
      if (busy) kicked = true;
      schedule(timings.startMs);
    },
    stop: () => stop("asked"),
    isRunning: () => running,
  };
}
